// Kanban columns (spec §13.6): a frame with layout 'column' stacks its cards
// top to bottom at its inner width. The layout is a pure function of the
// cards' order and heights, shared by the editor (which stores it as ordinary
// updates) and the Kanban template.
import { CARD } from './cards.ts';
import type { BoardObject, Frame } from './types.ts';

export const COLUMN = {
  pad: 16,          // round the cards
  gap: 12,          // between cards
  footer: 44,       // "+ Add card"
  minHeight: 200,
  rowGap: 120,      // columns at most this far apart (same top edge) form a row
  spacing: 40,      // between a new column and the one it was added beside
} as const;

export const isColumn = (o: BoardObject | undefined | null): o is Frame => o?.type === 'frame' && o.layout === 'column';

/** How wide cards are in a column this wide. */
export const cardWidthIn = (columnW: number) => Math.max(CARD.minWidth, columnW - 2 * COLUMN.pad);

interface Positioned { id: string; x: number; y: number; w: number; h: number }

/** Top to bottom by centre; ties by x, then id. */
export function byPosition<T extends Positioned>(cards: readonly T[]): T[] {
  return [...cards].sort((a, b) => (a.y + a.h / 2) - (b.y + b.h / 2) || (a.x + a.w / 2) - (b.x + b.w / 2) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export interface Stack {
  /** Each card's top edge, in order. */
  tops: number[];
  /** How tall the column needs to be for these cards. */
  height: number;
}

/**
 * Stacks cards of the given heights from the column's top. `gap` leaves room
 * of `size` before the card at `index` (index = heights.length: at the end).
 */
export function stack(top: number, heights: readonly number[], gap?: { index: number; size: number }): Stack {
  const tops: number[] = [];
  let y = top + COLUMN.pad;
  const room = () => { y += gap!.size + COLUMN.gap; };
  heights.forEach((h, i) => {
    if (gap && gap.index === i) room();
    tops.push(y);
    y += h + COLUMN.gap;
  });
  if (gap && gap.index >= heights.length) room();
  const used = y - top - (heights.length || gap ? COLUMN.gap : 0);
  return { tops, height: Math.max(COLUMN.minHeight, used + COLUMN.pad + COLUMN.footer) };
}

/**
 * Columns side by side form rows (the same top edge, at most rowGap apart),
 * which share the tallest height. Returns each row's column ids, left to right.
 */
export function rows(columns: readonly Frame[]): string[][] {
  const byTop = new Map<number, Frame[]>();
  for (const c of columns) {
    const key = Math.round(c.y);
    byTop.set(key, [...(byTop.get(key) ?? []), c]);
  }
  const out: string[][] = [];
  for (const group of byTop.values()) {
    group.sort((a, b) => a.x - b.x || (a.id < b.id ? -1 : 1));
    let row: Frame[] = [];
    for (const c of group) {
      const last = row.at(-1);
      if (last && c.x - (last.x + last.w) > COLUMN.rowGap) {
        out.push(row.map((r) => r.id));
        row = [];
      }
      row.push(c);
    }
    if (row.length) out.push(row.map((r) => r.id));
  }
  return out;
}
